import { useMemo } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";

/**
 * Lightweight markdown renderer for AI-generated step output. Agents emit
 * markdown by convention — headings, bold, bullets, code — so any string we
 * surface in the play UI should render it instead of leaking the raw `###`
 * and `**` syntax to the reader. Use this everywhere a step's text content
 * is shown; reach for ArticleRender only when you also need
 * selection-to-comment.
 */
export function MarkdownText({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  const html = useMemo(() => {
    const parsed = marked.parse(children ?? "", {
      async: false,
      breaks: true,
      gfm: true,
    }) as string;
    return DOMPurify.sanitize(parsed);
  }, [children]);
  return (
    <div
      className={className ? `md-render ${className}` : "md-render"}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/** True when a string carries any markdown that would render badly as plain text. */
export function hasMarkdown(s: string): boolean {
  if (!s) return false;
  return (
    /[#*_`]/.test(s) ||
    /^\s*[-+]\s/m.test(s) ||
    /^\s*\d+\.\s/m.test(s) ||
    /\n/.test(s)
  );
}
